import * as React from "react";
import { useSelector, useDispatch } from "react-redux";
import "./styles.css";
//internal and external components import
import { Table, Tooltip, Typography } from "@mui/material";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import StatusIndicatorSmall from "../StatusIndicatorSmall";
import ProgressBar from "../ProgressBar";
import {
  sortData,
} from "../../common/SortSupport";
import { EnhancedTableHead } from "../EnhancedTableHead";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLock,
  faUnlock,
  faUpRightAndDownLeftFromCenter,
} from "@fortawesome/free-solid-svg-icons";
import { Box } from "@mui/material";
import { CustomTooltip } from "../CustomTooltip";
import {
  setSelectedLicenceCriteria,
  setInitialSelectedLicenceCriteria,
  setLicenceCriteria,
  setInitialLicenceCriteriaStandards,
} from "../../redux/reducers/licence";
import TableSkeleton from "../TableSkeleton";
import { genUID, licenceCriteriaActionsRegex, licenceCriteriaEvidencesRegex, licenceCriterionRegex, licenceSelectedCriteriaActionsRegex, licenceSelectedStandardsActionsRegex, licenceSelectedStandardsRegex } from "../../common/StaticExports";
import CommentsDocsActionsIconWrapper from "../CommentsDocsActionsIconWrapper";
import { getAccessCount } from "../../common/logics";
import { useTranslation } from "react-i18next";
import { restrictCriteriaUsersLicence } from "../../api/licence";
import { setSnackBarData } from "../../redux/reducers/common";
import AccessManager from "../../containers/AccessManager";
import { useLocation, useNavigate } from "react-router";

export default function LicenceCriteriaTable(props: {
  data: any;
  licenceCriteria: any;
  selectedLicenceCriteria: any;
  triggerFetchLicenceCriteria?: any;
}) {
  const { data, licenceCriteria, selectedLicenceCriteria, triggerFetchLicenceCriteria } = props;
  const [t] = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const isAdmin = useSelector((state: any) => state.common.isAdmin);
  const clubDetails = useSelector((state:any) => state.clubDetails);
  const initialAccessState = {
    isOpen: false,
    itemId: null,
  };
  const [manageAccess, setManageAccess] = React.useState(initialAccessState);
  const userId = useSelector((state:any) => state.common.user)?.id;
  const [licenceCriteriaData, setLicenceCriteriaData] = React.useState(data);

  //supporting contents
  const [order, setOrder] = React.useState("");
  const [orderBy, setOrderBy] = React.useState("");

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: any
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const visibleRows:any = React.useMemo(
    () =>
      data?.length > 0 &&
      sortData(data, orderBy, order),
    [order, orderBy, data],
  );

  const head = isAdmin
    ? [
        {
          key: "uid",
          displayName: "id",
        },
        {
          key: "name",
          displayName: "criteria",
        },
        {
          key: "",
          displayName: "",
        },
        {
          key: "status",
          displayName: "status",
        },
        {
          key: "progress",
          displayName: "Club Progress",
        },
        {
          key: "evaluationProgress",
          displayName: "Evaluation Progress",
        },
      ]
    : [
        {
          key: "uid",
          displayName: "id",
        },
        {
          key: "name",
          displayName: "criteria",
        },
        {
          key: "",
          displayName: "",
        },
        {
          key: "progress",
          displayName: "Club Progress",
        },
        {
          key: "",
          displayName: "",
        },
      ];

  const manageTableExpand = () => {
    dispatch(setInitialSelectedLicenceCriteria());
    dispatch(setInitialLicenceCriteriaStandards());
    navigate(`/licence/criteria`);
  };

  const manageTableShrink = (clickedItem: any) => {
    dispatch(setInitialSelectedLicenceCriteria());
    dispatch(setInitialLicenceCriteriaStandards());
    dispatch(setSelectedLicenceCriteria(clickedItem));
    if (!(licenceCriterionRegex.test(location?.pathname) ||
        licenceSelectedStandardsRegex.test(location?.pathname) ||
        licenceCriteriaActionsRegex.test(location?.pathname) ||
        licenceSelectedCriteriaActionsRegex.test(location?.pathname) ||
        licenceCriteriaEvidencesRegex.test(location?.pathname) ||
        licenceSelectedStandardsActionsRegex.test(location?.pathname))) {
          navigate(`/licence/criteria/${clickedItem?.id}/standards`);
        }
  };
  const manageUserAccess = (isUpdated: any, restrictedUsers: any) => {
    if (isUpdated) {
      var updatedCriteria = data?.find((x:any) => x.id == manageAccess?.itemId);
      updatedCriteria = {...updatedCriteria, restrictions: updatedCriteria?.restrictions?.map((res:any) => {
        if(restrictedUsers?.includes(res.userId)) {
          return {...res, isRestricted: true}
        } return res;
      })};
      // setLicenceCriteriaData(licenceCriteriaData?.map((c:any) => c.id == updatedCriteria.id ? updatedCriteria : c));
      dispatch(setLicenceCriteria(data?.map((c:any) => c.id == updatedCriteria.id ? updatedCriteria : c)));
      restrictCriteriaUsersLicence({
        clubId: clubDetails?.id,
        criteriaId: manageAccess?.itemId,
        users: restrictedUsers,
      })
        .then(() => {
          const warningData: any = {
            isOpen: true,
            severity: "success",
            message: t("ACCESS_UPDATED_SUCCESSFULLY"),
          };
          dispatch(setSnackBarData(warningData));
          triggerFetchLicenceCriteria();
        })
        .catch((error: any) => {
          const warningData: any = {
            isOpen: true,
            severity: "error",
            message: error?.message || t("ACCESS_UPDATE_FAILED"),
          };
          dispatch(setSnackBarData(warningData));
          // setLicenceCriteriaData(data);
          dispatch(setLicenceCriteria(data));
        });
    }
    setManageAccess(initialAccessState);
  };

  //table contents

  return (
    <React.Fragment>
      <Box sx={{ position: "relative", height: "100%" }}>
        {!licenceCriteria && (
          <FontAwesomeIcon
            className="icon tableExpandIcon"
            icon={faUpRightAndDownLeftFromCenter}
            onClick={() => manageTableExpand()}
          />
        )}
        <AccessManager isOpen={manageAccess?.isOpen} handleIsOpen={manageUserAccess} from={'licenceCriteria'} itemId={manageAccess?.itemId} />
        <TableContainer
          sx={{
            maxHeight: 640,
            width: !licenceCriteria ? "fit-content" : "auto",
            overflow: licenceCriteria ? "auto" : "hidden",
          }}
        >
          <Table
            stickyHeader
            sx={{ width: licenceCriteria ? "100%" : "auto", boxShadow: "none" }}
            size="small"
            aria-label="a dense table"
          >
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              tableHeads={licenceCriteria ? head : [head[0]]}
            />
            <TableBody>
              {visibleRows?.length ? (
                visibleRows.map((d: any, i: number) => {
                  const totalUsers = d?.restrictions?.length || 0;
                  const accessCount = getAccessCount(d?.restrictions);
                  const isPrivate = totalUsers == accessCount ? false : true;
                  const restrictedUsers = d?.restrictions?.filter((res:any) => res.isRestricted)?.map((x:any) => x.userId) || [];
                  const isRestricted = restrictedUsers?.includes(userId);
                  return (
                    <TableRow
                      className={isRestricted ? 'selectedRow cursorNotAllowed' : 'hoverBG'}
                      key={`dynamic-table-row-${i}`}
                      onClick={() => !isRestricted && manageTableShrink(d)}
                    >
                      <CustomTooltip
                        title={d.name}
                        className={
                          !licenceCriteria ? "capitalize" : "displayNone"
                        }
                        arrow
                        followCursor
                        placement="top-start"
                      >
                        <TableCell
                          className={
                            !licenceCriteria &&
                            selectedLicenceCriteria.uid === d.uid
                              ? "fw900 "
                              : ""
                          }
                        >
                          {genUID(d.uid)}
                        </TableCell>
                      </CustomTooltip>
                      {licenceCriteria && (
                        <>
                          <TableCell
                            className="capitalize"
                            sx={{
                              cursor: isRestricted ? 'not-allowed' : 'pointer',
                              width: isAdmin ? "40%" : "60%",
                            }}
                          >
                            {/* <FontAwesomeIcon className='icon mr5' icon={faChevronRight} /> */}
                            {d.name}
                          </TableCell>
                          <TableCell className="fs12" sx={{ minWidth: '180px' }}>
                            <CommentsDocsActionsIconWrapper
                              actions={d.noOfActions || 0}
                              comments={
                                isAdmin
                                  ? d.noOfComments || 0
                                  : d.noOfClubComments || 0
                              }
                              docs={d.noOfEvidences || 0}
                              showDetailedCount={isAdmin}
                              jlComments={d.noOfJLComments}
                              clubComments={d.noOfClubComments}
                            />
                          </TableCell>
                          {isAdmin && (
                            <TableCell>
                              <StatusIndicatorSmall
                                reportType={"siteReview"}
                                status={d.status}
                              />
                            </TableCell>
                          )}
                          <TableCell>
                            <ProgressBar percent={d.progress} />
                          </TableCell>
                          {isAdmin && (
                            <TableCell>
                              <ProgressBar percent={d.evaluationProgress} />
                            </TableCell>
                          )}
                          {!isAdmin && (
                            <TableCell>
                              <Tooltip
                                title={!isRestricted && t("TDM_MANAGE_ACCESS")}
                                arrow
                                placement="top"
                                className={isPrivate ? "closedLock" : ""}
                              >
                                <span>
                                  <Typography className="fs12 lockCountLabel mr5">
                                    {isPrivate ? accessCount : ''}
                                  </Typography>
                                  <FontAwesomeIcon
                                    className={
                                      isPrivate ? "icon closedLock" : "icon"
                                    }
                                    icon={isPrivate ? faLock : faUnlock}
                                    onClick={(e: any) => {
                                      e.stopPropagation();
                                      !isRestricted &&
                                      setManageAccess({
                                        isOpen: true,
                                        itemId: d.id,
                                      });
                                    }}
                                  />
                                </span>
                              </Tooltip>
                            </TableCell>
                          )}
                        </>
                      )}
                    </TableRow>
                  );
                })
              ) : (
                <TableSkeleton rowCount={16} cellCount={head?.length} />
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </React.Fragment>
  );
}
